import classNames from "classnames"

import { Color } from "../../../../../constants/V2/color"
import PillButton from "../../../../elements/V2/Buttons/PillButton"
import Typography from "../../../../elements/V2/Typography"
import LayoutItem from "../../../Layouts/LayoutItem"

import {
  getAccentColor,
  getBackgroundColorClass,
  getBorderColorClass,
} from "@utils/V2/color"
import React from "react"

type Props = {
  backgroundColor: Color
  titleColor?: Color
  borderColor?: Color
  superscriptText?: string
  headerText: string
  subscriptText?: string
  bodyText?: string
  disclaimerText?: string
  disclaimerBackgroundColor?: Color
  linkText?: string
  linkUrl?: string | YocoCom.YocoLink
  linkColor?: Color
  size?: "small" | "medium" | "large"
  textAlignment: "top" | "center"
  mobileGridOrder?: number
  trackingEvent?: string
  trackingEventKey?: string
  trackingEventValue?: string
}

const TextInfo = ({
  backgroundColor,
  titleColor,
  borderColor,
  superscriptText,
  headerText,
  subscriptText,
  bodyText,
  disclaimerText,
  disclaimerBackgroundColor,
  linkText,
  linkUrl,
  linkColor,
  size = "large",
  textAlignment = "top",
  mobileGridOrder,
  trackingEvent,
  trackingEventKey,
  trackingEventValue,
  ...props
}: Props) => {
  const accentColor = getAccentColor(backgroundColor)
  const titleTextColor = titleColor || accentColor
  const cardBorderColor = borderColor || backgroundColor

  const cta = linkText && linkUrl && linkColor && (
    <PillButton
      text={linkText}
      color={linkColor}
      overrideTextColor={accentColor}
      linkUrl={linkUrl}
      style="bordered"
      size="large"
      trackingEvent={trackingEvent}
      trackingEventKey={trackingEventKey}
      trackingEventValue={trackingEventValue}
    />
  )

  const headerSize = size === "small" ? "h5" : "h4"
  const bodySize = "body-md"

  return (
    <LayoutItem
      mobileGridOrder={mobileGridOrder}
      as="div"
      animateOnScroll
      className={classNames(
        getBackgroundColorClass(backgroundColor),
        getBorderColorClass(cardBorderColor),
        "rounded-lg flex flex-col gap-20 border border-opacity-20 px-20 py-24 md:gap-24 md:px-24 md:py-32",
        { "col-span-full md:col-span-3 lg:col-span-3": size === "small" },

        {
          "col-span-full md:col-span-2 lg:col-span-4 lg:gap-24 lg:px-24 lg:py-40":
            size === "medium",
        },
        {
          "col-span-full md:col-span-3 lg:col-span-6 lg:gap-32 lg:px-32 lg:py-48":
            size === "large",
        },
        textAlignment === "top" ? "justify-between" : "justify-center"
      )}
      {...props}
    >
      {textAlignment === "top" ? (
        <>
          <div
            className={classNames("flex flex-col gap-20 md:gap-24", {
              "lg:gap-24": size === "medium",
              "lg:gap-32": size === "large",
            })}
          >
            <div
              className={classNames("space-y-16", {
                "lg:space-y-20": size !== "small",
              })}
            >
              {superscriptText && (
                <Typography
                  font="grotesk"
                  weight="book"
                  size="subscript-md"
                  text={superscriptText}
                  color={accentColor}
                  className="uppercase"
                />
              )}

              <Typography
                font="grotesk"
                weight="medium"
                size={headerSize}
                text={headerText}
                color={titleTextColor}
                element="h3"
              />
              {subscriptText && (
                <Typography
                  font="grotesk"
                  weight="book"
                  size="subscript-lg"
                  text={subscriptText}
                  color={accentColor}
                  className="uppercase"
                />
              )}
            </div>
            {bodyText && (
              <Typography
                font="grotesk"
                weight="book"
                size={bodySize}
                text={bodyText}
                color={accentColor}
              />
            )}

            {disclaimerText && (
              <div className="pt-20">
                <Typography
                  font="grotesk"
                  weight="book"
                  size="body-md"
                  text={disclaimerText}
                  color={accentColor}
                  className={classNames(
                    "rounded-8 border border-gray px-[30px] py-[22px]",
                    disclaimerBackgroundColor &&
                      getBackgroundColorClass(disclaimerBackgroundColor)
                  )}
                />
              </div>
            )}
          </div>
          <div className="flex w-full flex-col text-left">{cta}</div>
        </>
      ) : (
        <div
          className={classNames("space-y-20 md:space-y-24", {
            "lg:space-y-24": size === "medium",
            "lg:space-y-32": size === "large",
          })}
        >
          <div
            className={classNames("space-y-16", {
              "lg:space-y-20": size !== "small",
            })}
          >
            {superscriptText && (
              <Typography
                font="grotesk"
                weight="book"
                size="subscript-lg"
                text={superscriptText}
                color={accentColor}
                className="uppercase"
              />
            )}

            <Typography
              font="grotesk"
              weight="medium"
              size={headerSize}
              text={headerText}
              color={titleTextColor}
              element="h3"
            />

            {subscriptText && (
              <Typography
                font="grotesk"
                weight="book"
                size="subscript-lg"
                text={subscriptText}
                color={accentColor}
                className="uppercase"
              />
            )}

            {bodyText && (
              <Typography
                font="grotesk"
                weight="book"
                size={bodySize}
                text={bodyText}
                color={accentColor}
              />
            )}

            {disclaimerText && (
              <div className="pt-20">
                <Typography
                  font="grotesk"
                  weight="book"
                  size="body-md"
                  text={disclaimerText}
                  color={accentColor}
                  className={classNames(
                    "rounded-8 border border-gray px-[30px] py-[22px]",
                    disclaimerBackgroundColor &&
                      getBackgroundColorClass(disclaimerBackgroundColor)
                  )}
                />
              </div>
            )}
          </div>
          {cta}
        </div>
      )}
    </LayoutItem>
  )
}

export default TextInfo
